// Where the Record screen's sound comes from, chosen at the top in plain
// words: the room (the microphone), a call on this computer (the call's own
// sound, shared from a tab or the screen, mixed with the microphone), or the
// agent's notes bot sent into the call. Pure: which of these this browser can
// do, and what the screen says about it (findings in
// /var/tmp/ui-round3-2026-10-29/recorder/capture-support.md). In the Mac app
// the call's sound is the app's own capture of this Mac's sound (no share:
// nativeCallSound.ts), when the app says it can.

import { hasDesktopToken } from "@/native/desktop";
import { CALL_MAC_DENIED, CALL_MAC_UNAVAILABLE, nativeCallAudioAvailable } from "@/native/desktopCallAudio";

export type RecordSource = "room" | "computer" | "bot";

/**
 * What the call-sound mix is hearing while it records (recorderTypes.CallSoundLike):
 * "asking_mic" = the call's sound records while the microphone prompt waits for an answer.
 */
export type CallSoundState = "both" | "call_only" | "mic_only" | "asking_mic";

/** The call side quiet this long while the microphone hears speech: the wrong tab shared, said. */
export const CALL_SILENT_SEC = 60;

/** Where a call recording's call side comes from: a share (a browser), or the Mac app's own capture of this Mac's sound. */
export type CallVia = "share" | "native";

/** A phone or tablet (a touch-first screen, or the home-screen install of one): never a desktop app window. */
export function isPhoneDevice(o: { touch: boolean; standalone: boolean; ua: string; touchPoints: number }): boolean {
  if (o.touch) return true;
  // An installed app counts only on a phone or tablet: Corvus installed from Chrome or Edge on a Mac
  // or a PC is also "standalone", and can share a call there.
  const mobile = /Android|iPhone|iPad|iPod|Mobile/.test(o.ua) || (/Macintosh/.test(o.ua) && o.touchPoints > 1);
  return o.standalone && mobile;
}

export interface BrowserEnv {
  /** navigator.mediaDevices.getDisplayMedia exists. */
  displayMedia: boolean;
  ua: string;
  /** User-Agent Client Hints, when the browser gives them (Chromium). */
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  /** userAgentData.platform ("macOS", "Windows", "Chrome OS", "Linux"), else navigator.platform. */
  platform?: string;
  /** A touch-first device (phone, tablet, the phone shell). */
  touch?: boolean;
  /** In the Mac app, and the app can capture this Mac's sound itself (desktopCallAudio.ts). */
  nativeCallAudio?: boolean;
}

/**
 * Whether this browser can hear a call: `ok` with what it can share besides
 * a tab ("yes": the whole screen or a window with its sound, so the Zoom or
 * Teams app too; "maybe": macOS, where that needs Chrome 141 and macOS 14.2;
 * "no": a browser tab only), else why not. `native`: the Mac app records this
 * Mac's sound itself (no share).
 */
export type CallSupport =
  | { ok: true; screen: "yes" | "maybe" | "no"; native?: true }
  | { ok: false; why: "phone" | "macapp" | "safari" | "firefox" | "browser" };

function chromiumVersion(env: BrowserEnv): number | null {
  const brand = env.brands?.find((b) => /^(Chromium|Google Chrome|Microsoft Edge)$/.test(b.brand));
  if (brand) return Number.parseInt(brand.version, 10) || 0;
  if (/Firefox\//.test(env.ua)) return null;
  const m = env.ua.match(/(?:Chrome|Chromium|CriOS|Edg)\/(\d+)/);
  return m ? Number(m[1]) : null;
}

export function callSupport(env: BrowserEnv): CallSupport {
  const ua = env.ua;
  if (env.mobile || env.touch || /Android|iPhone|iPad|iPod|Mobile/.test(ua)) return { ok: false, why: "phone" };
  // The Mac app's web view is Safari's engine: no sound from a share, but the app may hear this Mac itself.
  if (hasDesktopToken(ua)) return env.nativeCallAudio ? { ok: true, screen: "yes", native: true } : { ok: false, why: "macapp" };
  if (/Firefox\//.test(ua)) return { ok: false, why: "firefox" };
  const version = chromiumVersion(env);
  if (version === null) return { ok: false, why: /Safari\//.test(ua) ? "safari" : "browser" };
  if (!env.displayMedia) return { ok: false, why: "browser" };
  const platform = (env.platform || ua).toLowerCase();
  if (/win|cros|chrome os/.test(platform)) return { ok: true, screen: "yes" };
  if (/mac/.test(platform)) return { ok: true, screen: version >= 141 ? "maybe" : "no" };
  return { ok: true, screen: "no" };
}

/** This browser, read once (only in a browser). */
export function browserEnv(touch: boolean): BrowserEnv {
  const nav = typeof navigator !== "undefined" ? navigator : undefined;
  const hints = (nav as unknown as { userAgentData?: { brands?: { brand: string; version: string }[]; mobile?: boolean; platform?: string } } | undefined)?.userAgentData;
  return {
    displayMedia: !!nav?.mediaDevices && typeof nav.mediaDevices.getDisplayMedia === "function",
    ua: nav?.userAgent ?? "",
    brands: hints?.brands,
    mobile: hints?.mobile,
    platform: hints?.platform || nav?.platform,
    touch,
    nativeCallAudio: nativeCallAudioAvailable(),
  };
}

/** The Mac app records this Mac's sound itself: no share, no picker. */
export function isNativeCall(s: CallSupport): boolean {
  return s.ok && s.native === true;
}

const cap = (s: string) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);

/**
 * The choices as the Segmented shows them: the call one only off a phone, the bot only where it
 * is set up. `compact`: the narrow card (the phone shell, or a desktop window half a screen wide),
 * short labels that never cut off.
 */
export function sourceOptions(o: { phone: boolean; compact: boolean; agentName: string; botAvailable?: boolean }): { value: RecordSource; label: string }[] {
  const name = cap(o.agentName);
  return [
    { value: "room", label: "In the room" },
    ...(o.phone ? [] : [{ value: "computer" as const, label: o.compact ? "This computer" : "Call on this computer" }]),
    ...(o.botAvailable === false ? [] : [{ value: "bot" as const, label: o.compact ? `Send ${name}` : `Send ${name} to the call` }]),
  ];
}

/** The one quiet line under the choice while nothing records ("" for the room: nothing to explain). */
export function sourceHint(source: RecordSource, s: { support: CallSupport; agentName: string; botAvailable: boolean }): string {
  const name = cap(s.agentName);
  if (source === "room") return "";
  if (source === "bot") {
    return s.botAvailable
      ? `${name} joins a Meet, Zoom or Teams call and takes notes. No microphone needed.`
      : "The notes bot isn't set up on this server yet.";
  }
  const sup = s.support;
  const bot = s.botAvailable;
  if (!sup.ok) {
    if (sup.why === "safari") return bot ? `Safari can't hear a call's sound. Use Chrome or Edge, or send ${name} to the call.` : "Safari can't hear a call's sound. Use Chrome or Edge.";
    if (sup.why === "firefox") return bot ? `Firefox can't hear a call's sound. Use Chrome or Edge, or send ${name} to the call.` : "Firefox can't hear a call's sound. Use Chrome or Edge.";
    if (sup.why === "phone") return bot ? `A phone can't share a call's sound. Send ${name} to the call instead.` : "A phone can't share a call's sound. Record it in the room.";
    if (sup.why === "macapp") return bot ? `The Corvus app can't hear a call's sound. Use Corvus in Chrome, or send ${name} to the call.` : "The Corvus app can't hear a call's sound. Use Corvus in Chrome.";
    return bot ? `This browser can't hear a call's sound. Send ${name} to the call instead.` : "This browser can't hear a call's sound. Use Chrome or Edge.";
  }
  if (isNativeCall(sup)) return "Records this Mac's sound and your microphone.";
  if (sup.screen === "yes") return "Pick the call's tab, or your screen for the Zoom or Teams app, with its sound on.";
  if (sup.screen === "maybe") return "Pick the call's tab with “Share tab audio” on. For the Zoom or Teams app, share your screen.";
  return bot ? `Pick the call's browser tab with “Share tab audio” on. For the Zoom or Teams app, send ${name}.` : "Pick the call's browser tab with “Share tab audio” on.";
}

/** Where the live waveform says the sound comes from. */
export function hearingLabel(callSound: CallSoundState | null, micLabel: string | null): string {
  if (callSound === "both") return "Call sound and microphone";
  if (callSound === "asking_mic") return "Call sound · waiting for the microphone";
  if (callSound === "call_only") return "Call sound only";
  if (callSound === "mic_only") return micLabel ? `Microphone only · ${micLabel}` : "Microphone only";
  return micLabel ? `Microphone · ${micLabel}` : "Microphone";
}

/**
 * A warning while a call recording has lost one of its two sides, or "": the share ended, the
 * microphone off or muted, its prompt still open, or (`callSilent`) a call that stays silent while
 * the microphone hears speech: the wrong tab, or one shared without its sound. In the Mac app
 * (`via` "native") the call side is this Mac's sound, and `lost` says the Mac stopped allowing it.
 */
export function callSoundWarning(callSound: CallSoundState | null, callSilent = false, via: CallVia = "share", lost: "denied" | null = null): string {
  const native = via === "native";
  if (callSound === "mic_only" && native) {
    return lost === "denied"
      ? "Corvus isn't allowed to record this Mac's sound, so only the microphone is recording."
      : "This Mac's sound stopped, so only the microphone is recording now.";
  }
  if (callSound === "mic_only") return "Sharing stopped, so only the microphone is recording now.";
  if (callSound === "call_only") return "The microphone isn't recording; the call's sound still is.";
  if (callSound === "asking_mic") return "Allow the microphone to record your side too.";
  if (callSound === "both" && callSilent && native) return "No sound from this Mac for a minute. Is the call playing, and is Corvus allowed to record it in System Settings?";
  if (callSound === "both" && callSilent) return "No sound from the call for a minute. Is it the right tab, with its sound on?";
  return "";
}

// What the call-sound capture throws (recorderMedia's callSound.ts), by name.
export const CALL_CANCELLED = "CallShareCancelled";
export const CALL_NO_SOUND = "CallNoSound";
export const CALL_UNSUPPORTED = "CallUnsupported";
export const CALL_BLOCKED = "CallBlocked";

export const CALL_CANCELLED_NOTICE = "Nothing was shared, so nothing is recording.";
export const CALL_NO_SOUND_NOTICE = "That share had no sound. Start again and turn on “Share tab audio” (or system audio).";

// The Mac app's own capture (desktopCallAudio.ts throws CALL_MAC_DENIED / CALL_MAC_UNAVAILABLE).
export const CALL_MAC_DENIED_LINE = "Corvus isn't allowed to record this Mac's sound. Turn it on in System Settings › Privacy & Security › Screen & System Audio Recording, then try again.";
export const CALL_MAC_FAILED_LINE = "Corvus couldn't hear this Mac's sound. Try again, or record in the room.";
export const CALL_MAC_NOT_BACK_NOTICE = "This Mac's sound didn't come back.";

/**
 * A failed start of a call recording in words: a quiet notice (the person
 * cancelled the picker, or shared without sound: Start tries again), a
 * failure, or null when it was not the share that failed (the microphone's
 * own words apply).
 */
export function callStartError(e: { name?: string } | null | undefined): { notice: string } | { failure: string } | null {
  const name = e?.name ?? "";
  if (name === CALL_CANCELLED) return { notice: CALL_CANCELLED_NOTICE };
  if (name === CALL_NO_SOUND) return { notice: CALL_NO_SOUND_NOTICE };
  if (name === CALL_UNSUPPORTED) return { failure: "This browser can't share a call's sound." };
  if (name === CALL_BLOCKED) return { failure: "Your computer is blocking screen sharing for this browser. Allow it in the system's privacy settings, then try again." };
  if (name === CALL_MAC_DENIED) return { failure: CALL_MAC_DENIED_LINE };
  if (name === CALL_MAC_UNAVAILABLE) return { failure: CALL_MAC_FAILED_LINE };
  return null;
}

/**
 * What a tap on "Share the call again" / "Hear this Mac again" came to, in one line, or null:
 * the Mac refusing, or its sound not coming back (the Mac app); a share without sound (a browser;
 * a cancelled picker says nothing).
 */
export function reshareNoteOf(got: string, via: CallVia | null): string | null {
  if (got === "blocked") return CALL_MAC_DENIED_LINE;
  if (via === "native") return got === "cancelled" ? CALL_MAC_NOT_BACK_NOTICE : null;
  return got === "no_sound" ? CALL_NO_SOUND_NOTICE : null;
}

/** Notices the screen shows as something to act on (amber), not as plain news. */
export const WARNING_NOTICES: readonly string[] = [CALL_NO_SOUND_NOTICE];
